import { AxiosProgressEvent } from 'axios'

import { useBlendVisionContext } from '@/context/BlendVisionContext'
import { completeUploadFile, createVod, uploadFile } from '@/server/services/blendvision'
import { FileSourceType, FileType, Part } from '@/server/services/types'
import { uploadParts } from '@/services/blendvision'

export type UploadVideoToBlendVisionServiceParams = {
  file: File
  index: number
  updateProgress?: (progress: number, index: number) => void
}

export const useUploadVideoToBlendVisionService = () => {
  const { profileSets } = useBlendVisionContext()

  const uploadVideoToBlendVisionService = async ({
    file,
    index,
    updateProgress,
  }: UploadVideoToBlendVisionServiceParams) => {
    console.time('⏰ uploadFile')

    const {
      file: { id: fileId },
      upload_data: { id: uploadId, parts },
    } = await uploadFile({
      file: {
        name: file.name,
        size: file.size.toString(),
        source: FileSourceType.Vod,
        type: FileType.Video,
      },
    })

    // console.log('✅ step 1: ', { fileId, uploadId, parts })
    console.timeEnd('⏰ uploadFile')

    // Setting up class properties with the API response for further use.
    const uploadPartSize = parts.length ?? 0
    // Calculating the size of each chunk based on total file size and part count.
    const chunkSize = file.size / uploadPartSize

    console.time('⏰ uploadParts')

    let totalUploaded = 0

    function onProgress(progressEvent: AxiosProgressEvent, chunk: Blob) {
      let progress: number
      // Check if total is defined
      if (progressEvent.total) {
        progress = (progressEvent.loaded / progressEvent.total) * chunk.size
      } else {
        // If total is undefined, use loaded bytes directly
        progress = progressEvent.loaded
      }

      totalUploaded += progress - (totalUploaded % chunk.size)

      // Call updateProgress callback with calculated progress
      if (updateProgress) {
        updateProgress(Math.floor(Math.floor((totalUploaded / file.size) * 10000) / 100), index)
      }
    }

    // Collect all upload promises
    const chunks = (await Promise.all(
      parts.map((part, i) => {
        const { part_number, presigned_url } = part
        const chunk = file.slice(chunkSize * i, chunkSize * (i + 1))
        return uploadParts({
          presignedUrl: presigned_url,
          partNumber: part_number,
          chunk,
          onProgress: (progressEvent) => onProgress(progressEvent, chunk),
        })
      }),
    )) as Part[]

    // console.log('✅ step 2: ', { chunks })
    console.timeEnd('⏰ uploadParts')

    console.time('⏰ completeUploadFile')

    const data = await completeUploadFile({
      id: fileId,
      complete_data: {
        id: uploadId,
        parts: chunks,
      },
    })

    // console.log('✅ step 3: ', { data })
    console.timeEnd('⏰ completeUploadFile')

    console.time('⏰ createVod')

    const vod = await createVod({
      // 🧬 remove file extension
      name: data.file.name.replace(/\.\w+$/, ''),
      profileSetId: profileSets[0]?.id,
      sourceId: data.file.id,
    })

    // console.log('✅ step 4: ', { vod })

    console.timeEnd('⏰ createVod')

    return vod
  }

  return { uploadVideoToBlendVisionService }
}
